import { LocalStorageKey } from 'shared/constants/localStorage'

import type { LoggerService } from '../LoggerService'
import { summarizeToken, summarizeUserStorageValue } from './tokenInfo'

// Keep only fingerprints, never credentials, in diagnostic bookkeeping.
const receivedPairs = new Map<string, string>()
let storageReason: string | undefined

export const getAuthStorageReason = () => storageReason ?? 'unspecified'

export function withAuthStorageReason<T>(
  reason: string,
  operation: () => T
): T {
  const previous = storageReason
  storageReason = reason
  try {
    return operation()
  } finally {
    storageReason = previous
  }
}

function storedPairs() {
  return {
    user: summarizeUserStorageValue(localStorage.getItem(LocalStorageKey.USER)),
    bootstrap: summarizeUserStorageValue(
      localStorage.getItem(LocalStorageKey.BOOTSTRAP_TOKENS)
    )
  }
}

function pairKey(access: string, refresh: string) {
  return `${summarizeToken(access)?.fp}:${summarizeToken(refresh)?.fp}`
}

function parseBody(body: unknown): Record<string, unknown> | undefined {
  try {
    const parsed = typeof body === 'string' ? JSON.parse(body) : body
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, unknown>
    }
  } catch {
    // A malformed or non-JSON response is itself useful evidence.
  }
  return undefined
}

export function isAuthRefreshRequest(url: string | undefined, method?: string) {
  if (!url || method !== 'POST' || !process.env.REACT_APP_AUTH_ENDPOINT)
    return false
  try {
    const endpoint = new URL(
      process.env.REACT_APP_AUTH_ENDPOINT,
      window.location.href
    )
    const actual = new URL(url, window.location.href)
    const path = `${endpoint.pathname.replace(/\/$/, '')}/refresh`
    return actual.origin === endpoint.origin && actual.pathname === path
  } catch {
    return false
  }
}

export function startRefreshDiagnostic(
  logger: LoggerService,
  transport: 'fetch' | 'xhr',
  body: unknown
) {
  const attemptId = crypto.randomUUID()
  // Instrumentation must never make an auth request fail.
  try {
    const parsed = parseBody(body)
    const submitted = parsed?.refresh_token
    const sent =
      typeof submitted === 'string' ? summarizeToken(submitted) : undefined
    logger.track('auth.token_refresh_started', {
      attemptId,
      transport,
      stack: new Error().stack
    })
    logger.track('auth.token_refresh_credentials_selected', {
      attemptId,
      sent,
      bodyReadable: !!parsed,
      stored: storedPairs()
    })
  } catch {
    // The normal request and logger health reporting remain authoritative.
  }
  return attemptId
}

export function receiveRefreshDiagnostic(
  logger: LoggerService,
  attemptId: string,
  status: number,
  body: unknown,
  correlation: { requestId?: string; traceId?: string } = {}
) {
  try {
    const parsed = parseBody(body)
    const data = parseBody(parsed?.data)
    const access = data?.access_token
    const refresh = data?.refresh_token
    const validPair =
      typeof access === 'string' &&
      !!access &&
      typeof refresh === 'string' &&
      !!refresh
    if (status >= 200 && status < 300 && validPair) {
      receivedPairs.set(pairKey(access, refresh), attemptId)
      if (receivedPairs.size > 50)
        receivedPairs.delete(receivedPairs.keys().next().value!)
    }
    logger.track('auth.token_refresh_response_received', {
      attemptId,
      status,
      ...correlation,
      bodyReadable: !!parsed,
      validPair,
      received: {
        access: typeof access === 'string' ? summarizeToken(access) : undefined,
        refresh:
          typeof refresh === 'string' ? summarizeToken(refresh) : undefined
      }
    })
  } catch {
    // Never surface diagnostic failures to the auth flow.
  }
}

// Called synchronously after AUTH_TOKEN_UPDATED listeners have run. Compare
// actual strings, not hashes: fingerprints are only for the exported evidence.
export function verifyRefreshPersistence(
  logger: LoggerService,
  access: string,
  refresh: string,
  source: string
): boolean {
  try {
    const currentUser = parseBody(localStorage.getItem(LocalStorageKey.USER))
    const bootstrap = parseBody(
      localStorage.getItem(LocalStorageKey.BOOTSTRAP_TOKENS)
    )
    const success =
      !!access &&
      !!refresh &&
      (currentUser?.access_token ?? bootstrap?.access_token) === access &&
      (currentUser?.refresh_token ?? bootstrap?.refresh_token) === refresh
    const payload = {
      attemptId: receivedPairs.get(pairKey(access, refresh)),
      source,
      success,
      expected: {
        access: summarizeToken(access),
        refresh: summarizeToken(refresh)
      },
      stored: storedPairs()
    }
    logger.track('auth.token_refresh_storage_verified', payload)
    if (success) logger.track('auth.token_refresh_completed', payload)
    else logger.error('auth.token_refresh_persistence_failed', payload)
    return success
  } catch {
    logger.error('auth.token_refresh_persistence_failed', {
      source,
      reason: 'storage_verification_unavailable'
    })
    return false
  }
}
